import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Clock, CheckCircle2, XCircle, MinusCircle, Activity } from "lucide-react";
import adminApi from "@/lib/adminApi";

/**
 * Admin > Cron — health panel for the Iter27 HTTP-cron model.
 * Shows per-job 24h counts (success/skipped/error), average success duration,
 * current lock state and the most recent 50 runs. Read-only — external cron
 * (or an operator with CRON_TOKEN) is what triggers the actual jobs.
 */
const JOB_META = {
  daily_summary:        { label: "Daily summary",      desc: "20:00 Lagos — today's sales recap to opted-in users" },
  weekly_summary:       { label: "Weekly summary",     desc: "Sun 19:00 Lagos — 7-day PNG snapshot (20 min TTL)" },
  low_stock_alert:      { label: "Low-stock alert",    desc: "Mon-Sat 09:00 Lagos — restock nudge" },
  debtor_reminder:      { label: "Debtor reminder",    desc: "Mon 10:00 Lagos — debts overdue >30 days" },
  personal_reminders:   { label: "Personal reminders", desc: "Every 10 min — user-scheduled nudges" },
  loan_repayment_check: { label: "Loan repayment",     desc: "Daily 11:00 Lagos — auto-debit + follow-up" },
};

const STATUS_TONE = {
  success: "text-emerald-700 bg-emerald-50 border-emerald-200",
  skipped: "text-amber-700 bg-amber-50 border-amber-200",
  error:   "text-rose-700 bg-rose-50 border-rose-200",
};
const STATUS_ICON = {
  success: CheckCircle2,
  skipped: MinusCircle,
  error:   XCircle,
};

export default function AdminCron() {
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (soft = false) => {
    if (soft) setRefreshing(true); else setLoading(true);
    try {
      const [s, r] = await Promise.all([
        adminApi.get("/admin/cron/summary"),
        adminApi.get(`/admin/cron/runs?limit=50${filter ? `&job=${filter}` : ""}`),
      ]);
      setSummary(s.data);
      setRuns(r.data.items || []);
    } catch {
      /* soft-fail */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so an active job's counts update while operators watch.
  useEffect(() => {
    const t = setInterval(() => load(true), 30 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !summary) {
    return <div className="p-6 text-sm text-muted-foreground">Loading cron health…</div>;
  }

  const jobs = summary?.jobs || [];

  return (
    <div className="p-6 space-y-6" data-testid="admin-cron-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-kudi-green" /> Cron Health
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Last 24 h · counts per job · recent runs · current lock state.
            External cron (with <code className="text-[11px] bg-muted px-1 rounded">X-Cron-Token</code>) drives execution.
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-white hover:bg-accent/40 text-sm font-medium disabled:opacity-40"
          data-testid="cron-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Per-job summary grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {jobs.map((j) => (
          <JobCard key={j.job} job={j} onFilter={() => setFilter(j.job)} isFiltered={filter === j.job} />
        ))}
      </div>

      {/* Recent runs table */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-heading font-bold text-lg">Recent runs {filter ? `— ${JOB_META[filter]?.label || filter}` : ""}</div>
            <div className="text-[11px] text-muted-foreground">{runs.length} row(s)</div>
          </div>
          {filter && (
            <button
              onClick={() => setFilter("")}
              className="text-xs font-semibold text-kudi-green hover:underline"
              data-testid="cron-clear-filter-btn"
            >
              Clear filter
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cron-runs-table">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Started</th>
                <th className="text-left px-4 py-2 font-semibold">Job</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-right px-4 py-2 font-semibold">Duration</th>
                <th className="text-left px-4 py-2 font-semibold">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {runs.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
                    No runs recorded yet.
                  </td>
                </tr>
              )}
              {runs.map((r) => {
                const Icon = STATUS_ICON[r.status] || MinusCircle;
                return (
                  <tr key={r.id} data-testid={`cron-run-${r.id}`}>
                    <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {formatWhen(r.started_at)}
                    </td>
                    <td className="px-4 py-2 font-medium">{r.job}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_TONE[r.status] || ""}`}>
                        <Icon className="w-3 h-3" />
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-xs tabular-nums">
                      {r.took_seconds != null ? `${r.took_seconds.toFixed(3)}s` : "—"}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-[280px]">
                      {r.error || r.skipped_reason || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function JobCard({ job, onFilter, isFiltered }) {
  const meta = JOB_META[job.job] || { label: job.job, desc: "" };
  const c = job.counts_24h || {};
  const lock = job.lock;
  const last = job.last_run;
  const LastIcon = last ? STATUS_ICON[last.status] || Clock : Clock;
  return (
    <button
      onClick={onFilter}
      className={`text-left bg-white border rounded-2xl p-4 hover:border-kudi-green/40 transition ${
        isFiltered ? "border-kudi-green ring-2 ring-kudi-green/20" : "border-border"
      }`}
      data-testid={`cron-job-card-${job.job}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-sm truncate">{meta.label}</div>
          <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{meta.desc}</div>
        </div>
        {lock && (
          <span
            className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full whitespace-nowrap"
            title={`Held by ${lock.holder} until ${lock.expires_at}`}
            data-testid={`cron-lock-badge-${job.job}`}
          >
            LOCKED
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <Counter label="Success" value={c.success || 0} tone="emerald" />
        <Counter label="Skipped" value={c.skipped || 0} tone="amber" />
        <Counter label="Errors"  value={c.error || 0}   tone="rose" />
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <LastIcon className="w-3.5 h-3.5" />
          {last ? `${last.status} · ${formatWhen(last.started_at)}` : "no runs yet"}
        </div>
        <div>
          {job.avg_success_seconds_24h != null
            ? `avg ${job.avg_success_seconds_24h.toFixed(2)}s`
            : "—"}
        </div>
      </div>
    </button>
  );
}

const TONES = {
  emerald: "text-emerald-700 bg-emerald-50",
  amber:   "text-amber-700 bg-amber-50",
  rose:    "text-rose-700 bg-rose-50",
};

function Counter({ label, value, tone }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 ${TONES[tone] || ""}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums leading-tight">{value}</div>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
